import PayoutBatchItemsList from '@/components/billing/payout-batch-items-list'
import { useCallback, useState } from 'react'
import { MdClose } from 'react-icons/md'
import { formatCurrency } from '../format'

interface PayoutBatchItemsModalProps {
    batchId: number
    recipientName?: string | null
    onClose: () => void
}

/**
 * O que há dentro de uma solicitação agrupada, para quem vai aprovar.
 *
 * A solicitação mostra um valor somado de um período inteiro — aprovar sem ver a
 * composição é aprovar um número.
 *
 * **Só leitura.** Quem muda a composição é o financeiro, na edição da solicitação,
 * antes de mandá-la para cá: tirar um lançamento neste ponto mudaria o valor debaixo
 * de quem está aprovando.
 */
export default function PayoutBatchItemsModal({
    batchId,
    recipientName,
    onClose,
}: PayoutBatchItemsModalProps) {
    // Os totais são do lote inteiro e vêm da própria lista, que os recebe do banco —
    // somar a página aberta anunciaria vinte repasses de um lote de mil.
    const [summary, setSummary] = useState({ total: 0, totalAmount: 0 })
    const handleSummary = useCallback(
        (next: { total: number; totalAmount: number }) => setSummary(next),
        []
    )

    return (
        <dialog open className="modal modal-open">
            <div className="modal-box max-w-2xl">
                <div className="flex items-start justify-between gap-3 mb-1">
                    <div>
                        <h3 className="font-bold text-lg">Repasses agrupados</h3>
                        <p className="text-sm text-base-content/60">
                            Solicitação #{batchId}
                            {recipientName ? ` · ${recipientName}` : ''}
                        </p>
                    </div>
                    <button
                        className="btn btn-sm btn-circle btn-ghost"
                        onClick={onClose}
                        aria-label="Fechar"
                    >
                        <MdClose size={18} />
                    </button>
                </div>

                <div className="max-h-[55vh] overflow-y-auto mt-3">
                    <PayoutBatchItemsList
                        batchId={batchId}
                        onSummary={handleSummary}
                    />
                </div>

                {summary.total > 0 && (
                    <div className="flex items-center justify-between gap-3 mt-4 px-4 py-3 rounded-xl bg-base-200/60">
                        <span className="text-sm">
                            {summary.total}{' '}
                            {summary.total === 1 ? 'repasse' : 'repasses'}
                        </span>
                        <span className="font-bold">
                            {formatCurrency(summary.totalAmount)}
                        </span>
                    </div>
                )}

                <div className="modal-action">
                    <button className="btn btn-ghost" onClick={onClose}>
                        Fechar
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </dialog>
    )
}
